import { useEffect, useState } from "react";
import { Coins, Loader2, Calendar, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const fmtFjd = (n) => `FJ$${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Math.round((n || 0) * 100) / 100)}`;

export default function AgentPayouts() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) { navigate("/login?next=/agent/payouts"); return; }
        (async () => {
            try {
                const res = await api.get("/agent/payouts");
                setData(res.data);
            } finally {
                setLoading(false);
            }
        })();
    }, [user, navigate]);

    if (!user) return null;

    return (
        <div data-testid="agent-payouts-page" className="bg-background min-h-screen">
            <section className="bg-ocean-deep-grad text-white py-10 lg:py-12">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-3">
                        <Coins className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            JV Payout Ledger
                        </span>
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight mb-2">
                        Your kickbacks &amp; deal splits.
                    </h1>
                    <p className="font-app text-sm sm:text-base text-white/85 max-w-3xl">
                        Every JV you originated or co-listed shows here once it's financially closed.
                        Pending → Paid → archived. Co-listing splits applied automatically.
                    </p>
                </div>
            </section>

            <section className="py-10 lg:py-12">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <div className="text-center py-16"><Loader2 className="h-8 w-8 animate-spin text-coral mx-auto" /></div>
                    ) : (
                        <>
                            {/* Summary */}
                            <div className="grid grid-cols-3 gap-3 mb-8" data-testid="payouts-summary">
                                <SummaryCard
                                    icon={Sparkles}
                                    label="Lifetime"
                                    value={fmtFjd(data?.summary?.total_lifetime_fjd || 0)}
                                    accent="coral"
                                />
                                <SummaryCard
                                    icon={AlertCircle}
                                    label="Pending"
                                    value={fmtFjd(data?.summary?.total_pending_fjd || 0)}
                                    accent="muted"
                                />
                                <SummaryCard
                                    icon={CheckCircle2}
                                    label="Paid"
                                    value={fmtFjd(data?.summary?.total_paid_fjd || 0)}
                                    accent="green"
                                />
                            </div>

                            {/* Ledger */}
                            <h2 className="font-display text-xl font-bold text-foreground mb-3">Ledger</h2>
                            {!data?.items?.length ? (
                                <Card className="p-8 text-center bg-card">
                                    <Coins className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                                    <p className="font-app text-sm text-muted-foreground">
                                        No payouts yet. They'll appear here when one of your JVs is marked financially closed by an admin.
                                    </p>
                                </Card>
                            ) : (
                                <div className="space-y-2" data-testid="payout-rows">
                                    {data.items.map((p) => (
                                        <Card key={p.id} className="p-4 bg-card flex items-center gap-3 flex-wrap" data-testid={`payout-${p.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                                    <span className="font-display text-sm font-bold text-foreground truncate">{p.jv_title || "JV deal"}</span>
                                                    {p.role === "primary" ? (
                                                        <Badge variant="outline" className="text-[10px] border-coral/40 text-coral">Primary</Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="text-[10px] border-fiji-green/40 text-fiji-green">Co-agent</Badge>
                                                    )}
                                                    <StatusBadge status={p.status} />
                                                </div>
                                                <div className="flex items-center gap-3 flex-wrap text-[11px] font-app text-muted-foreground">
                                                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Closed {new Date(p.closed_at).toLocaleDateString()}</span>
                                                    {p.paid_at && <span>· Paid {new Date(p.paid_at).toLocaleDateString()}</span>}
                                                    <span>· {p.pct_of_total_split}% of {fmtFjd(p.total_kickback_fjd)} pool</span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-display text-lg font-bold text-foreground tabular-nums">{fmtFjd(p.amount_fjd)}</div>
                                                <div className="font-app text-[10px] uppercase tracking-wider text-muted-foreground">{p.currency}</div>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}

function SummaryCard({ icon: Icon, label, value, accent }) {
    const accentMap = {
        coral: "bg-coral/5 dark:bg-coral/10 border-coral/30 text-coral",
        green: "bg-fiji-green/5 dark:bg-fiji-green/10 border-fiji-green/30 text-fiji-green",
        muted: "bg-card border-border text-muted-foreground",
    };
    return (
        <Card className={`p-4 ${accentMap[accent] || ""}`}>
            <div className="flex items-center gap-2 mb-1.5">
                <Icon className="h-3.5 w-3.5" />
                <span className="font-app text-[10px] uppercase tracking-wider">{label}</span>
            </div>
            <div className="font-display text-2xl font-bold tabular-nums text-foreground">
                {value}
            </div>
        </Card>
    );
}

function StatusBadge({ status }) {
    if (status === "paid") return <Badge className="bg-fiji-green text-white text-[10px]">Paid</Badge>;
    if (status === "cancelled") return <Badge variant="outline" className="text-[10px] border-destructive/30 text-destructive">Cancelled</Badge>;
    return <Badge variant="outline" className="text-[10px] border-coral/40 text-coral">Pending</Badge>;
}
